import React, { useState } from "react";
import { Info, X } from "lucide-react";

/**
 * Compact 7-pill rank ladder with a "?" button that expands a full
 * legend explaining each rank's promotion requirement.
 *
 * Used inline in the PATH TO X panel on the Command page.
 */
const LADDER = [
  { idx: 0, name: "Grunt",         req: "Start here." },
  { idx: 1, name: "Squad Leader",  req: "Grow your horde to 51." },
  { idx: 2, name: "Lieutenant",    req: "Grow your horde to 301." },
  { idx: 3, name: "Warlord",       req: "Grow your horde to 1,001." },
  { idx: 4, name: "Crown Prince",  req: "Grow your horde to 3,001." },
  { idx: 5, name: "King of the Dead", req: "Defeat the King at the Bone Throne." },
  { idx: 6, name: "Emperor of the Dead", req: "Control 11 regions on the world map." },
];

export default function RankLadder({ currentRankIdx = 0 }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-3" data-testid="rank-ladder">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 flex-1 overflow-x-auto" data-testid="rank-ladder-pills">
          {LADDER.map((r, i) => {
            const reached = i <= currentRankIdx;
            const isCurrent = i === currentRankIdx;
            return (
              <React.Fragment key={r.idx}>
                <div
                  className={`h-2 flex-1 min-w-[16px] rounded-sm transition-all ${
                    isCurrent ? "bg-blood ring-1 ring-blood/40" :
                    reached ? "bg-bone/70" :
                    "bg-[var(--border-subtle)]"
                  }`}
                  title={`${r.name} — ${r.req}`}
                  data-testid={`rank-ladder-pill-${r.idx}`}
                />
                {i < LADDER.length - 1 && (
                  <span className={`text-[0.6rem] font-mono ${
                    i < currentRankIdx ? "text-bone/40" : "text-muted-h/40"
                  }`}>›</span>
                )}
              </React.Fragment>
            );
          })}
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="text-muted-h hover:text-blood transition-colors p-1 -m-1"
          aria-label={open ? "Hide rank ladder" : "Show rank ladder"}
          data-testid="rank-ladder-toggle"
        >
          {open ? <X size={12} /> : <Info size={12} />}
        </button>
      </div>

      {open && (
        <ol className="mt-3 space-y-1.5 fade-up" data-testid="rank-ladder-legend">
          {LADDER.map((r, i) => {
            const isCurrent = i === currentRankIdx;
            const reached = i < currentRankIdx;
            return (
              <li
                key={r.idx}
                className={`flex items-baseline gap-3 px-2 py-1.5 border-l-2 ${
                  isCurrent ? "border-blood bg-[#1a0606]" :
                  reached ? "border-bone/30" :
                  "border-[var(--border-subtle)]"
                }`}
                data-testid={`rank-ladder-row-${r.idx}`}
              >
                <span className={`font-mono text-[0.7rem] tabular-nums w-4 ${
                  isCurrent ? "text-blood" : reached ? "text-bone/60" : "text-muted-h"
                }`}>
                  {String(r.idx + 1).padStart(2, "0")}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`font-serif-h text-sm ${
                    isCurrent ? "text-bone" :
                    reached ? "text-bone/60 line-through decoration-bone/30" :
                    "text-muted-h"
                  }`}>
                    {r.name}
                    {isCurrent && <span className="text-blood ml-2 text-[0.65rem] font-mono">— YOU ARE HERE</span>}
                  </p>
                  <p className={`text-[0.65rem] tracking-wider mt-0.5 ${
                    isCurrent ? "text-[var(--text-main)]" : "text-muted-h"
                  }`}>
                    {r.req}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
